import clsx from 'clsx';
import React, { FC, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TbPhoto, TbPhotoStar, TbPlus } from 'react-icons/tb';

import { useAddRestaurantPictureMutation, useDeleteRestaurantPictureMutation, useUpdateRestaurantPictureMutation } from '@/api/hooks/useRestaurant';
import { ISerializedPicture } from '@/api/lib/restaurant';

import { RichTypography } from '@/components/atoms/RichTypography';
import { Spinner } from '@/components/atoms/Spinner';
import { Typography } from '@/components/atoms/Typography';
import OpenableImage from '@/components/molecules/OpenableImage/OpenableImage';

import { PictureMenuItem } from './PictureMenuItem';
import { pictureScore } from '@/utils/pictureScore';
import { PictureScoreLabel } from '@/components/molecules/PictureScoreLabel';

type Props = {
  pictures?: ISerializedPicture[];
  restaurantId: string;
  thereIsNoCertificate?: boolean;
};

export const RestaurantPicturesGallery: FC<Props> = ({ pictures, restaurantId, thereIsNoCertificate }) => {
  const { t } = useTranslation();
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const { register, setValue } = useForm();
  const { ref: registerRef, ...registerProps } = register('picture');

  const [selectedPictureId, setSelectedPictureId] = useState<string | null>(null);

  const { isLoading: isDeleting, mutateAsync: deleteRestaurantPicture } = useDeleteRestaurantPictureMutation({
    onSuccess: () => {
      setSelectedPictureId(null);
      toast.success(t('action.pictureRemoved'), {
        position: 'bottom-right'
      });
    }
  });

  const { isLoading: isUpdating, mutateAsync: updateMainPictureRestaurant } = useUpdateRestaurantPictureMutation({
    onSuccess: () => {
      setSelectedPictureId(null);
      document.getElementById('header')?.scrollIntoView({ behavior: 'smooth' });
      toast.success(t('action.mainPictureUpdated'), {
        position: 'bottom-right'
      });
    }
  });

  const { isLoading: isUploading, mutateAsync: addRestaurantPicture } = useAddRestaurantPictureMutation({
    onSuccess: () => {
      toast.success(t('action.newPictureUpdated'), {
        position: 'bottom-right'
      });
    }
  });

  const handleDeletePictureRestaurant = async (pictureId: string) => {
    setSelectedPictureId(pictureId);
    await deleteRestaurantPicture({ restaurantId, pictureId });
  };

  const handleSetPictureAsMain = async (pictureId: string) => {
    setSelectedPictureId(pictureId);
    await updateMainPictureRestaurant({ restaurantId, pictureId, principal: true });
  };

  const handleUploadedFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && restaurantId) {
      const file = files[0];
      setValue('picture', file);

      await addRestaurantPicture({
        restaurantId,
        file
      });
    }
  };

  const handleClickToUpload = () => {
    hiddenInputRef?.current?.click();
  };

  return (
    <div className="md:max-w-6xl mx-auto">
      <section className="border border-gray-300 gap-5 p-4 md:p-10 rounded-xl">
        <Typography variant="h1" weight="semibold" marginClassName="mb-5">
          {t('picturePage.title')}
        </Typography>

        {pictures?.find((picture) => picture.principal) ? (
          <div className="flex relative md:gap-10 flex-col md:flex-row">
            {pictures
              .filter((picture) => picture.principal)
              .map((picture) => (
                <div key={picture.sizes.thumbnail?.url} className="ring-primary flex ring-[3px] p-1 items-center rounded-xl">
                  <OpenableImage
                    picture={picture.sizes.thumbnail?.url ?? ''}
                    height={160}
                    width={208}
                    className="w-full h-40 md:h-72 object-cover rounded-lg"
                  />
                </div>
              ))}
            <div className="space-y-5 mt-2">
              <div className="md:ml-5">
                <Typography variant="h3" weight="medium">
                  {t('picturePage.mainImageBlock.1')}
                </Typography>
                <div className="ml-4">
                  <RichTypography variant="paragraph" color="gray">
                    {t('picturePage.mainImageBlock.2')}
                  </RichTypography>
                  <RichTypography variant="paragraph" color="gray">
                    {t('picturePage.mainImageBlock.3')}
                  </RichTypography>
                </div>
              </div>
              <div className="md:ml-5">
                <Typography variant="h3" weight="medium">
                  {t('picturePage.mainImageBlock.4')}
                </Typography>
                <div className="ml-4">
                  <RichTypography variant="paragraph" color="gray">
                    {t('picturePage.mainImageBlock.5')}
                  </RichTypography>
                  <RichTypography variant="paragraph" color="gray">
                    {t('picturePage.mainImageBlock.6')}
                  </RichTypography>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Typography variant="small" color="gray">
            {t('picturePage.noMainPicture')}
          </Typography>
        )}
      </section>

      <section className="flex flex-col gap-10 mt-12">
        <div className="space-y-5 mt-2">
          <div className="flex items-baseline flex-col md:flex-row justify-between">
            <Typography variant="h1" weight="semibold" marginClassName="mb-5">
              {t('picturePage.secondTitle')}
            </Typography>
            <button
              onClick={handleClickToUpload}
              disabled={isUploading || thereIsNoCertificate}
              className="hover:cursor-pointer flex items-center gap-1 disabled:cursor-not-allowed w-fit hover:border-primary duration-200 justify-center ease-in-out hover:text-white p-2 border rounded-lg  hover:bg-primary/80 border-gray-300 text-black"
            >
              {isUploading ? (
                <>
                  <Spinner reverse />
                  {t('action.uploading')}
                </>
              ) : (
                <>
                  <TbPlus className="text-xl" />
                  Ajouter
                </>
              )}
              {thereIsNoCertificate && <div className="text-red-500 font-bold">- Certificat manquant</div>}
            </button>
          </div>
          <div className="md:ml-10">
            <Typography variant="h3" weight="medium">
              {t('picturePage.galeryImageBlock.1')}
            </Typography>
            <div className="ml-4">
              <RichTypography variant="paragraph" color="gray">
                {t('picturePage.galeryImageBlock.2')}
              </RichTypography>
            </div>
          </div>
          <div className="md:ml-10">
            <Typography variant="h3" weight="medium">
              {t('picturePage.galeryImageBlock.3')}
            </Typography>
            <div className="ml-4">
              <RichTypography variant="paragraph" color="gray">
                {t('picturePage.galeryImageBlock.4')}
              </RichTypography>
            </div>
          </div>
          <div className="md:ml-10">
            <Typography variant="h3" weight="medium">
              {t('picturePage.galeryImageBlock.5')}
            </Typography>
            <div className="ml-4">
              <RichTypography variant="paragraph" color="gray">
                {t('picturePage.galeryImageBlock.6')}
              </RichTypography>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5 justify-center gap-y-5 gap-x-5 md:gap-y-10">
          {pictures?.map((picture) => (
            <div
              key={picture.sizes.thumbnail?.url}
              className={clsx(picture.principal ? 'ring-primary ring-[3px]' : 'ring-gray-300 ring-[1px]', 'relative w-full rounded-xl')}
            >
              {picture.score && <PictureScoreLabel score={picture.score} />}
              <div className="p-2 mt-1">
                {picture.principal && (
                  <div className="flex items-center gap-1 absolute bg-primary/60 backdrop-blur-md rounded-md m-1 px-2 py-1">
                    <TbPhotoStar size={16} className="text-white" />
                    <Typography color="white" variant="extraSmall">
                      Image principale
                    </Typography>
                  </div>
                )}
                <OpenableImage
                  picture={picture.sizes.standardResolution?.url ?? ''}
                  height={160}
                  width={208}
                  className="w-full h-40 md:h-52 object-cover rounded-lg"
                />

                <PictureMenuItem
                  pictureId={picture.id}
                  alreadyMainPicture={picture.principal}
                  isLoading={picture.id === selectedPictureId && (isUpdating || isDeleting)}
                  handleDeletePictureRestaurant={handleDeletePictureRestaurant}
                  handleSetPictureAsMain={handleSetPictureAsMain}
                  thereIsNoCertificate={thereIsNoCertificate}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-5 md:mt-14">
        <input
          type="file"
          className="hidden"
          {...registerProps}
          ref={(e) => {
            registerRef(e);
            hiddenInputRef.current = e;
          }}
          accept=".png, .jpg, .jpeg"
          onChange={handleUploadedFile}
        />
        <button
          onClick={handleClickToUpload}
          disabled={isUploading || thereIsNoCertificate}
          className="hover:cursor-pointer flex-col md:flex-row disabled:cursor-not-allowed max-w-6xl w-full mx-auto flex gap-2 hover:border-primary duration-200 justify-center ease-in-out hover:text-white px-5 py-3 border rounded-lg  hover:bg-primary/80 items-center border-gray-300 text-black"
        >
          {isUploading ? (
            <>
              <Spinner reverse big />
              {t('action.uploading')}
            </>
          ) : (
            <>
              <TbPhoto className="text-xl" />
              {t('action.addNewPicture')}
            </>
          )}
          {thereIsNoCertificate && <div className="text-red-500 font-bold">- Certificat manquant</div>}
        </button>
      </section>
    </div>
  );
};
